/**
 * SagaRow
 * Top-level row for a saga workflow in the WorkflowList.
 * Shows unit name, aggregate progress, and a dismiss button.
 * Clicking the row drills into MemberChainView (level 1).
 */

import { realUnitNumber } from '@resida/shared';
import { ChevronRight } from 'lucide-react';
import { DismissWorkflowButton } from './DismissWorkflowButton';
import { useWorkflowPanel } from './useWorkflowPanel';
import { SAGA_FALLBACK_LABELS, type SagaWorkflowSummary } from '../../types/workflow';

interface SagaRowProps {
  saga: SagaWorkflowSummary;
  onDrillIn: (saga: SagaWorkflowSummary) => void;
}

export function SagaRow({ saga, onDrillIn }: SagaRowProps) {
  const { dismiss } = useWorkflowPanel();

  // unitLabel is null when the unit has no label. The visible title uses the UI fallback; the
  // aria-text omits the unit entirely (so it reads "move-in workflow", not the redundant
  // "Move-in move-in workflow"). Both are saga-type-aware so a lease_end saga never reads as
  // "Move-in" (issue #1027).
  const unitLabel = realUnitNumber(saga.unitLabel);
  const fallbackLabel = SAGA_FALLBACK_LABELS[saga.type];
  const workflowNoun = `${fallbackLabel.toLowerCase()} workflow`;
  const titleLabel = unitLabel ?? fallbackLabel;
  const openLabel = unitLabel ? `View ${unitLabel} ${workflowNoun}` : `View ${workflowNoun}`;
  const dismissLabel = unitLabel ? `Dismiss ${unitLabel} workflow` : `Dismiss ${workflowNoun}`;
  const completedCount = saga.members.filter((m) => m.status === 'completed').length;
  const totalCount = saga.members.length;
  const progressPercent = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  // Active member for inline status
  const activeMember = saga.members.find((m) => m.status === 'active');
  const unsatisfiedCount = activeMember
    ? activeMember.preconditions.filter((p) => !p.satisfied).length
    : 0;

  return (
    <div
      data-testid={`workflow-saga-row-${saga.id}`}
      className="relative flex items-center gap-3 p-3 rounded-lg border border-border bg-background"
      role="listitem"
    >
      {/* Main clickable area */}
      <button
        type="button"
        onClick={() => onDrillIn(saga)}
        className="flex-1 flex items-center gap-3 text-left min-w-0 hover:bg-accent/30 rounded-md transition-colors"
        aria-label={openLabel}
      >
        {/* Progress ring indicator */}
        <div
          className="relative shrink-0 w-9 h-9"
          aria-hidden="true"
        >
          <svg viewBox="0 0 36 36" className="w-9 h-9 -rotate-90">
            <circle
              cx="18" cy="18" r="15"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              className="text-muted/40"
            />
            <circle
              cx="18" cy="18" r="15"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              strokeDasharray={`${progressPercent * 0.942} 94.2`}
              className="text-primary transition-all duration-500"
            />
          </svg>
          {/* text-[10px]: intentionally sub-xs to fit inside the 36px progress ring; no Tailwind token equivalent */}
          <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-foreground">
            {completedCount}/{totalCount}
          </span>
        </div>

        {/* Text content */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground truncate">{titleLabel}</p>
          <p className="text-xs text-muted-foreground">
            {completedCount === totalCount
              ? 'Complete'
              : activeMember
              ? `${unsatisfiedCount > 0 ? `${unsatisfiedCount} item${unsatisfiedCount !== 1 ? 's' : ''} needed · ` : ''}${progressPercent}% done`
              : `${progressPercent}% done`}
          </p>
        </div>

        <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden="true" />
      </button>

      {/* Dismiss button — saga-level */}
      <DismissWorkflowButton
        workflowId={saga.id}
        accessibleLabel={dismissLabel}
        testId={`workflow-dismiss-btn-${saga.id}`}
        onDismiss={dismiss}
      />
    </div>
  );
}
